import React, { useEffect, useState } from "react";
import { Loader } from "@hopper/loading";
import { Select } from "@hopper/select";
import pluralize from "pluralize";
import { TestCase } from "types/test_cases";
import TestCasesList from "./test_cases_list";
import {
  IntegrationCode,
  fetchProjects,
  fetchTestCycles,
  fetchTestExecutions,
  mapProjects,
  mapTestCycles,
} from "./integration_app";
import { useIntegrationApp } from "@integration-app/react";
import { useField, useFormikContext } from "formik";
import { TextInput } from "components/flexible_testing/shared/form";
import { RequestedTest } from "components/flexible_testing/test_request_page/requested_test_page/requested_test_form";

type TestCasesImportFormProps = {
  integrationCode: IntegrationCode;
};

const TestCasesImportForm: React.FC<TestCasesImportFormProps> = ({ integrationCode }) => {
  const integrationApp = useIntegrationApp();
  const [isLoaded, setIsLoaded] = useState(true);
  const [projects, , { setValue: setProjects }] = useField("projects");
  const [testCycles, , { setValue: setTestCycles }] = useField("testCycles");
  const [projectId, setProjectId] = useState<string>();
  const [testCases, , { setValue, setTouched }] = useField<Array<TestCase> | null>("testCases");
  const { values } = useFormikContext<RequestedTest>();
  const setTestCases = (testCases: TestCase[] | null) => {
    setTouched(true);
    setValue(testCases);
  };

  const setChosenProject = (projectValue: string) => {
    setTestCycles([]);
    setTestCases(null);

    if (projectValue !== "") {
      setProjectId(projectValue);
      setIsLoaded(false);
      fetchTestCycles(integrationApp, integrationCode, { project_value: projectValue }, setIsLoaded, setTestCycles);
    }
  };

  const setChosenTestCycle = (testCycleValue: string) => {
    setTestCases(null);

    if (testCycleValue !== "") {
      setIsLoaded(false);

      fetchTestExecutions(
        integrationApp,
        integrationCode,
        { test_cycle_value: testCycleValue, project_value: projectId },
        setIsLoaded,
        setTestCases,
      );
    }
  };

  useEffect(() => {
    if (projects.value.length === 0) {
      setIsLoaded(false);
    }
    fetchProjects(integrationApp, integrationCode, setProjects, setIsLoaded);
  }, [integrationCode]);

  return (
    <div className="text-sm">
      <Loader isLoaded={isLoaded} />
      <div className="mb-xl">
        <div className="mb-xs">Please select the project</div>
        <Select
          onChange={event => {
            setChosenProject(event.target.value);
          }}
          size="md"
          options={[{ value: "", label: "" }, ...mapProjects(projects.value)]}
        />
      </div>
      <div className="mb-xs">Please select the test cycle</div>
      <Select
        onChange={event => {
          setChosenTestCycle(event.target.value);
        }}
        size="md"
        options={[{ value: "", label: "" }, ...mapTestCycles(testCycles.value)]}
      />
      {testCases.value && (
        <div className="mt-xl">
          <div>
            <i className="fa fa-check text-success" />
            <span className="pl-xs" data-testid="test-cases-count-message">{`You have imported ${
              testCases.value.length
            } ${pluralize("test case", testCases.value.length)}`}</span>
          </div>
          <TestCasesList testCases={testCases.value} />
        </div>
      )}
      <div className="mt-xl">
        <TextInput
          name="numberOfResultsPerTestCaseNeeded"
          value={values.numberOfResultsPerTestCaseNeeded}
          label="How many results do you need for each test case?"
          labelClass="font-normal"
          required
        />
      </div>
    </div>
  );
};

export default TestCasesImportForm;
